import { plainText } from '../plain-text'

export const RESTART_TIMING = {
  soupServe: 200,
  soupLine: 900,
  soupSweep: 1900,
  soupImpact: 2250,
  soupEnd: 3400,
  cutImpact: 4700,
  memoriesStart: 5600,
  end: 9000
} as const

export const RESTART_DURATION = RESTART_TIMING.end
export type RestartMemory = { title: string; choice: string }

const clamp01 = (value: number) => Math.max(0, Math.min(1, value))
const easeOutBack = (value: number) => {
  const p = clamp01(value) - 1
  return 1 + 2.70158 * p * p * p + 1.70158 * p * p
}

function drawSoupBowl(ctx: CanvasRenderingContext2D, elapsed: number) {
  const enter = easeOutBack((elapsed - RESTART_TIMING.soupServe) / 800)
  if (enter <= 0) return
  const tip = clamp01((elapsed - RESTART_TIMING.soupImpact) / 550)
  const opacity = tip < .72 ? 1 : 1 - (tip - .72) / .28

  ctx.save()
  ctx.globalAlpha = Math.max(0, opacity)
  ctx.translate(-56 * tip, -40 + (1 - enter) * 110 + 64 * tip)
  ctx.scale(.7 + .3 * enter, .7 + .3 * enter)
  ctx.rotate(-98 * Math.PI / 180 * tip)

  const steamOpacity = .35 + Math.sin(elapsed / 250) * .2
  ctx.strokeStyle = `rgba(200,230,215,${steamOpacity})`
  ctx.lineWidth = 3
  ctx.lineCap = 'round'
  for (const x of [-28, 0, 28]) {
    ctx.beginPath()
    ctx.moveTo(x, -36)
    ctx.quadraticCurveTo(x + 8, -48, x, -58)
    ctx.stroke()
  }

  ctx.fillStyle = '#5d5148'
  ctx.beginPath()
  ctx.moveTo(-66, 0)
  ctx.quadraticCurveTo(0, 18, 66, 0)
  ctx.lineTo(38, 48)
  ctx.quadraticCurveTo(0, 64, -38, 48)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#4a4038'
  ctx.beginPath()
  ctx.moveTo(-66, 0)
  ctx.quadraticCurveTo(0, 18, 66, 0)
  ctx.lineTo(60, 8)
  ctx.quadraticCurveTo(0, 24, -60, 8)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#8fd8ae'
  ctx.strokeStyle = '#6db98c'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.ellipse(0, 0, 66, 15, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.stroke()
  ctx.fillStyle = 'rgba(185,236,209,.72)'
  ctx.beginPath(); ctx.ellipse(-18, -3, 14, 4, 0, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = 'rgba(109,185,140,.65)'
  ctx.beginPath(); ctx.ellipse(18, 2, 10, 3, 0, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

function drawSweepingHand(ctx: CanvasRenderingContext2D, elapsed: number) {
  const sweep = clamp01((elapsed - RESTART_TIMING.soupSweep) / 420)
  if (sweep <= 0) return
  ctx.save()
  ctx.translate(-300 + 410 * sweep, -32)
  ctx.scale(.65, .65)
  ctx.rotate((-14 + 22 * sweep) * Math.PI / 180)
  ctx.fillStyle = '#e5bca1'
  ctx.strokeStyle = '#b88e74'
  ctx.lineWidth = 3
  ctx.shadowColor = 'rgba(0,0,0,.55)'
  ctx.shadowBlur = 10
  ctx.beginPath(); ctx.roundRect(-125, -5, 135, 55, 22); ctx.fill(); ctx.stroke()
  ctx.beginPath(); ctx.roundRect(-20, -40, 85, 100, 25); ctx.fill(); ctx.stroke()
  for (let index = 0; index < 4; index += 1) {
    ctx.beginPath(); ctx.roundRect(index * 18 - 10, -72, 16, 60, 8); ctx.fill(); ctx.stroke()
  }
  ctx.restore()
}

function drawSoupSplash(ctx: CanvasRenderingContext2D, elapsed: number) {
  const age = elapsed - RESTART_TIMING.soupImpact
  if (age < 0 || age > 1150) return
  const progress = age / 1150
  const colors = ['#9fd8b8', '#6fbf98', '#d6f0e2', '#4a9d7e']
  for (let index = 0; index < 56; index += 1) {
    const angle = index * 2.399
    const speed = 45 + (index % 8) * 11
    const x = Math.cos(angle) * speed * progress
    const y = 18 + Math.sin(angle) * speed * progress + 92 * progress * progress
    ctx.save()
    ctx.globalAlpha = 1 - progress
    ctx.translate(x, y)
    ctx.rotate(angle + progress * 3)
    ctx.fillStyle = colors[index % colors.length]
    ctx.beginPath()
    ctx.ellipse(0, 0, 3 + index % 3, 7 + index % 5, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }
}

function drawLegacySoupScene(ctx: CanvasRenderingContext2D, elapsed: number, reduced: boolean) {
  const shakeAge = elapsed - RESTART_TIMING.soupImpact
  if (!reduced && shakeAge >= 0 && shakeAge < 400) {
    const strength = 1 - shakeAge / 400
    ctx.translate(Math.sin(shakeAge * .13) * 12 * strength, Math.cos(shakeAge * .17) * 5 * strength)
  }
  drawSoupSplash(ctx, elapsed)
  drawSoupBowl(ctx, elapsed)
  drawSweepingHand(ctx, elapsed)

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = '700 17px KaiTi, STKaiti, STZhongsong, serif'
  ctx.fillStyle = '#cfe6da'
  ctx.shadowColor = 'rgba(0,0,0,.85)'
  ctx.shadowBlur = 10
  const firstLineIn = clamp01((elapsed - RESTART_TIMING.soupLine) / 400)
  const firstLineOut = 1 - clamp01((elapsed - RESTART_TIMING.soupImpact) / 180)
  if (firstLineIn * firstLineOut > 0) {
    ctx.globalAlpha = firstLineIn * firstLineOut
    ctx.fillText('孟婆：喝了这碗汤，忘掉上一世的丢人现眼…', 0, 130, 370)
  }
  const secondLineIn = clamp01((elapsed - RESTART_TIMING.soupImpact) / 400)
  if (secondLineIn > 0) {
    ctx.globalAlpha = secondLineIn
    ctx.fillText('这碗我不喝，我记得上一世', 0, 130, 370)
  }
  ctx.globalAlpha = 1
  ctx.shadowBlur = 0
}

export function drawRestartFrame(ctx: CanvasRenderingContext2D, width: number, height: number, elapsed: number, memories: RestartMemory[], reduced: boolean) {
  const t = reduced ? RESTART_TIMING.memoriesStart + 1000 : elapsed
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = t < RESTART_TIMING.soupEnd
    ? '#000'
    : t < RESTART_TIMING.memoriesStart ? '#202238' : '#edf1e7'
  ctx.fillRect(0, 0, width, height)
  ctx.save(); ctx.translate(width / 2, height / 2)
  const scale = Math.min(width / 420, height / 700, 1.4)
  ctx.scale(scale, scale)
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  const text = (value: string, y: number, size: number, color = '#344b40') => {
    ctx.fillStyle = color; ctx.font = `800 ${size}px sans-serif`; ctx.fillText(value, 0, y, 350)
  }

  if (t < RESTART_TIMING.soupEnd) {
    drawLegacySoupScene(ctx, t, reduced)
  } else {
    const originalTime = t - (RESTART_TIMING.soupEnd - 2600)
    if (originalTime < 4800) {
      const p = Math.min(1, (originalTime - 2600) / 1500)
      ctx.fillStyle='#202238';ctx.fillRect(-500,-800,1000,1600)
      if (!reduced) ctx.translate(Math.sin(originalTime*.06)*(1-p)*8,Math.cos(originalTime*.07)*(1-p)*5)
      for(let i=0;i<18;i++) {
        const x=((i*79)%380)-190, y=((originalTime*.2+i*97)%650)-325
        ctx.save();ctx.translate(x,y);ctx.rotate(i*.7+originalTime*.001)
        ctx.fillStyle='#e8ecc8';ctx.fillRect(-20,-10,40,20);ctx.fillStyle='#76896c';ctx.font='10px sans-serif';ctx.fillText('汤券',0,0);ctx.restore()
      }
      text('反手收购 · 忘川汤业',-220,24,'#e1efbf')
      text('喝汤？我直接割汤厂！',-154,30,'#fff')
      text('孟婆总资产',-54,17,'#adb7c2')
      text('¥ '+Math.round(99999999*(1-p)).toLocaleString('en-US'),4,45,'#f2e5ac')
      ctx.save();ctx.rotate(-.12);ctx.strokeStyle='#ff7567';ctx.lineWidth=5;ctx.strokeRect(-155,80,310,82)
      text(p>=1?'倾 家 荡 产':'资 产 清 算 中',122,37,'#ff7567');ctx.restore()
      text('孟婆：汤没喝，家没了。',235,23,'#e6e6ed')
    } else {
      const cards = memories.length ? memories : [{title:'这一世还没开始',choice:'就决定重来。'}]
      const index = Math.min(cards.length-1,Math.floor((originalTime-4800)/((RESTART_DURATION-6300)/cards.length)))
      const memory = cards[index]
      text('这些事，我可没忘。', -200, 29)
      ctx.fillStyle='#fffdf8';ctx.strokeStyle='#c6d1c0';ctx.lineWidth=2
      ctx.beginPath();ctx.roundRect(-180,-125,360,245,20);ctx.fill();ctx.stroke()
      text(`上一世 · ${index+1} / ${cards.length}`, -90, 13, '#8b9980')
      text(plainText(memory.title), -35, 25)
      ctx.fillStyle='#64755f';ctx.font='16px sans-serif'
      const chars=[...plainText(memory.choice)];let line='';let y=15
      for (const char of chars) { if(ctx.measureText(line+char).width>304){ctx.fillText(line,0,y);line='';y+=27;if(y>80)break} line+=char }
      if(y<=80)ctx.fillText(line,0,y)
      text(t > 8100 ? '带着记忆，再活一次。' : '这一次，换个活法。', 195, 22)
    }
  }
  ctx.restore()
}
